/**
 * @file session-liveness.ts
 * @description Single shared decider for "is this game session alive?"
 *
 * Wraps `GET /api/sessions/<id>/exists` with a 3s default timeout and a
 * fail-open contract: any error (timeout, 5xx, network blip, missing config)
 * resolves `true`. We never take a healthy session down because of an HTTP
 * hiccup — the surrounding state machines (WS close codes, retry caps) bound
 * permanent failures.
 */

const DEFAULT_TIMEOUT_MS = 3000;

interface IsSessionAliveOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

export async function isSessionAlive(
  sessionId: string,
  opts: IsSessionAliveOptions = {}
): Promise<boolean> {
  const gameServerUrl = import.meta.env.VITE_GAME_SERVER_URL;
  if (!gameServerUrl) return true;

  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const signals = [AbortSignal.timeout(timeoutMs)];
  if (opts.signal) signals.push(opts.signal);
  const signal = AbortSignal.any(signals);

  try {
    const res = await fetch(`${gameServerUrl}/api/sessions/${sessionId}/exists`, { signal });
    if (!res.ok) return true;
    const data = (await res.json()) as { exists?: boolean };
    return data.exists !== false;
  } catch {
    return true;
  }
}
